import React, { useEffect, useState } from 'react'

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0a0a0f;
    --paper: #f5f2eb;
    --accent: #e8471d;
    --gold: #c9a84c;
    --muted: #8a8578;
    --card-bg: #faf8f3;
    --border: rgba(10,10,15,0.1);
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--paper);
    color: var(--ink);
    overflow-x: hidden;
  }

  body::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 9999;
  }

  /* ── NAV ── */
  .cv-nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 5vw;
    background: rgba(245,242,235,0.9);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border);
    transition: padding 0.3s;
  }

  .cv-nav-logo {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 1.2rem;
    color: var(--ink);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: -0.02em;
  }

  .cv-nav-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--accent);
    flex-shrink: 0;
  }

  .cv-nav-links {
    display: flex;
    align-items: center;
    gap: 32px;
    list-style: none;
  }

  .cv-nav-links a {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--muted);
    text-decoration: none;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    transition: color 0.2s;
    white-space: nowrap;
  }

  .cv-nav-links a:hover { color: var(--ink); }

  .cv-nav-cta {
    background: var(--ink) !important;
    color: var(--paper) !important;
    padding: 9px 20px;
    border-radius: 100px;
    font-size: 0.78rem !important;
    font-weight: 600 !important;
    transition: background 0.2s, transform 0.2s !important;
    white-space: nowrap;
  }
  .cv-nav-cta:hover { background: var(--accent) !important; transform: translateY(-1px) !important; }

  /* Hamburger */
  .cv-hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    cursor: pointer;
    padding: 4px;
    background: none;
    border: none;
    z-index: 1001;
  }
  .cv-hamburger span {
    display: block;
    width: 24px; height: 2px;
    background: var(--ink);
    border-radius: 2px;
    transition: all 0.3s;
    transform-origin: center;
  }
  .cv-hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
  .cv-hamburger.open span:nth-child(2) { opacity: 0; }
  .cv-hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

  /* Mobile menu */
  .cv-mobile-menu {
    display: none;
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: var(--paper);
    z-index: 999;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 36px;
  }
  .cv-mobile-menu.open { display: flex; }
  .cv-mobile-menu a {
    font-family: 'Syne', sans-serif;
    font-size: 2rem;
    font-weight: 700;
    color: var(--ink);
    text-decoration: none;
    letter-spacing: -0.02em;
    transition: color 0.2s;
  }
  .cv-mobile-menu a:hover { color: var(--accent); }
  .cv-mobile-cta {
    background: var(--ink);
    color: var(--paper) !important;
    padding: 14px 36px;
    border-radius: 100px;
    font-size: 1rem !important;
  }

  /* ── HERO ── */
  .cv-hero {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 40px;
    padding: 120px 5vw 80px;
    position: relative;
    overflow: hidden;
  }

  .cv-hero-left { position: relative; z-index: 1; }

  .cv-hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 24px;
    animation: fadeUp 0.6s ease both;
  }
  .cv-hero-eyebrow::before {
    content: '';
    width: 24px; height: 1px;
    background: var(--accent);
  }

  .cv-hero-title {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(2.8rem, 6vw, 7rem);
    line-height: 0.95;
    letter-spacing: -0.03em;
    color: var(--ink);
    animation: fadeUp 0.6s 0.1s ease both;
  }

  .cv-hero-title em {
    font-style: normal;
    color: var(--accent);
    position: relative;
    display: inline-block;
  }
  .cv-hero-title em::after {
    content: '';
    position: absolute;
    bottom: 2px; left: 0; right: 0;
    height: 5px;
    background: var(--gold);
    opacity: 0.4;
    border-radius: 2px;
    z-index: -1;
  }

  .cv-hero-sub {
    font-size: 1rem;
    color: var(--muted);
    max-width: 420px;
    line-height: 1.75;
    margin-top: 24px;
    animation: fadeUp 0.6s 0.2s ease both;
  }

  .cv-hero-actions {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-top: 36px;
    flex-wrap: wrap;
    animation: fadeUp 0.6s 0.3s ease both;
  }

  .cv-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--ink);
    color: var(--paper);
    padding: 14px 28px;
    border-radius: 100px;
    font-size: 0.88rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.25s;
    white-space: nowrap;
  }
  .cv-btn-primary:hover {
    background: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(232,71,29,0.28);
  }

  .cv-btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--ink);
    font-size: 0.88rem;
    font-weight: 500;
    text-decoration: none;
    border-bottom: 1px solid var(--border);
    padding-bottom: 2px;
    transition: all 0.2s;
    white-space: nowrap;
  }
  .cv-btn-ghost:hover { color: var(--accent); border-color: var(--accent); }

  /* ── CV MOCKUP ── */
  .cv-hero-right {
    display: flex;
    justify-content: center;
    align-items: center;
    animation: floatIn 0.8s 0.3s ease both;
  }

  .cv-mockup-wrap {
    width: 100%;
    max-width: 400px;
    animation: float 6s 1s ease-in-out infinite;
  }

  .cv-mockup {
    background: white;
    border-radius: 18px;
    box-shadow: 0 40px 80px rgba(10,10,15,0.16), 0 0 0 1px rgba(10,10,15,0.05);
    padding: 28px;
    position: relative;
  }
  .cv-mockup::before {
    content: '';
    position: absolute;
    top: 0; left: 20px; right: 20px;
    height: 3px;
    background: linear-gradient(90deg, var(--accent), var(--gold));
    border-radius: 0 0 3px 3px;
  }

  .cvm-header { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 22px; }
  .cvm-avatar {
    width: 48px; height: 48px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), var(--gold));
    flex-shrink: 0;
  }
  .cvm-name { font-family: 'Syne', sans-serif; font-size: 1rem; font-weight: 700; color: var(--ink); }
  .cvm-role { font-size: 0.7rem; color: var(--muted); margin-top: 2px; }
  .cvm-section { margin-bottom: 18px; }
  .cvm-section-label {
    font-size: 0.58rem; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--accent); margin-bottom: 8px;
  }
  .cvm-bar-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .cvm-bar-label { font-size: 0.65rem; color: var(--ink); width: 72px; flex-shrink: 0; }
  .cvm-bar-track { flex: 1; height: 5px; background: #f0ede4; border-radius: 3px; overflow: hidden; }
  .cvm-bar-fill { height: 100%; background: linear-gradient(90deg, var(--accent), var(--gold)); border-radius: 3px; }
  .cvm-line { height: 7px; background: #f0ede4; border-radius: 4px; margin-bottom: 5px; }
  .cvm-line.s { width: 55%; }
  .cvm-line.m { width: 78%; }
  .cvm-badge-row { display: flex; flex-wrap: wrap; gap: 5px; }
  .cvm-badge {
    font-size: 0.6rem; padding: 3px 9px;
    background: #f0ede4; border-radius: 100px;
    color: var(--ink); font-weight: 500;
  }

  /* ── STATS ── */
  .cv-stats {
    background: var(--ink);
    padding: 36px 5vw;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .cv-stat {
    text-align: center;
    padding: 8px;
  }
  .cv-stat-num {
    font-family: 'Syne', sans-serif;
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    font-weight: 800;
    color: var(--paper);
    letter-spacing: -0.04em;
    line-height: 1;
  }
  .cv-stat-num b { color: var(--accent); font-weight: 800; }
  .cv-stat-label {
    font-size: 0.72rem;
    color: rgba(245,242,235,0.45);
    margin-top: 6px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  /* ── SECTION COMMONS ── */
  .cv-section-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 16px;
  }
  .cv-section-tag::before {
    content: '';
    width: 18px; height: 1px;
    background: currentColor;
  }

  .cv-section-heading {
    font-family: 'Syne', sans-serif;
    font-size: clamp(1.8rem, 3.5vw, 3rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.1;
    color: var(--ink);
    margin-bottom: 56px;
  }

  /* ── STEPS ── */
  .cv-steps-section {
    padding: 100px 5vw;
  }

  .cv-steps-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px;
    background: var(--border);
    border: 1px solid var(--border);
    border-radius: 20px;
    overflow: hidden;
  }

  .cv-step-card {
    background: var(--card-bg);
    padding: 40px 28px;
    transition: background 0.3s;
  }
  .cv-step-card:hover { background: white; }

  .cv-step-num {
    font-family: 'Syne', sans-serif;
    font-size: 3rem;
    font-weight: 800;
    color: rgba(10,10,15,0.05);
    line-height: 1;
    margin-bottom: 16px;
  }
  .cv-step-icon {
    width: 42px; height: 42px;
    border-radius: 11px;
    background: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    margin-bottom: 18px;
  }
  .cv-step-title {
    font-family: 'Syne', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 10px;
  }
  .cv-step-desc { font-size: 0.85rem; color: var(--muted); line-height: 1.65; }

  /* ── FEATURES ── */
  .cv-features-section {
    background: var(--ink);
    padding: 100px 5vw;
    position: relative;
    overflow: hidden;
  }
  .cv-features-section::before {
    content: 'RESUME';
    position: absolute;
    font-family: 'Syne', sans-serif;
    font-size: 22vw;
    font-weight: 800;
    color: rgba(255,255,255,0.022);
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    white-space: nowrap;
    pointer-events: none;
    user-select: none;
  }
  .cv-features-section .cv-section-tag { color: rgba(245,242,235,0.35); }
  .cv-features-section .cv-section-tag::before { background: rgba(245,242,235,0.3); }
  .cv-features-section .cv-section-heading { color: var(--paper); margin-bottom: 48px; }

  .cv-features-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .cv-feature-card {
    border: 1px solid rgba(245,242,235,0.07);
    border-radius: 16px;
    padding: 32px 28px;
    transition: all 0.3s;
    cursor: default;
    position: relative;
    overflow: hidden;
  }
  .cv-feature-card::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(232,71,29,0.07), transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }
  .cv-feature-card:hover { border-color: rgba(232,71,29,0.3); transform: translateY(-4px); }
  .cv-feature-card:hover::after { opacity: 1; }
  .cv-feature-icon { font-size: 1.5rem; margin-bottom: 16px; display: block; }
  .cv-feature-title { font-family: 'Syne', sans-serif; font-size: 0.95rem; font-weight: 700; color: var(--paper); margin-bottom: 8px; }
  .cv-feature-desc { font-size: 0.82rem; color: rgba(245,242,235,0.42); line-height: 1.65; }

  /* ── TESTIMONIALS ── */
  .cv-testimonials-section {
    padding: 100px 5vw;
    background: var(--card-bg);
  }
  .cv-testimonials-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  .cv-testimonial-card {
    background: white;
    border-radius: 16px;
    padding: 28px;
    border: 1px solid var(--border);
    transition: transform 0.3s, box-shadow 0.3s;
  }
  .cv-testimonial-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(10,10,15,0.08);
  }
  .cv-testimonial-stars { color: var(--gold); font-size: 0.85rem; margin-bottom: 14px; letter-spacing: 2px; }
  .cv-testimonial-text { font-size: 0.88rem; color: var(--ink); line-height: 1.7; margin-bottom: 20px; font-style: italic; }
  .cv-testimonial-author { display: flex; align-items: center; gap: 10px; }
  .cv-testimonial-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), var(--gold));
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    color: white;
    font-weight: 700;
    font-family: 'Syne', sans-serif;
  }
  .cv-testimonial-name { font-size: 0.82rem; font-weight: 600; color: var(--ink); }
  .cv-testimonial-role { font-size: 0.72rem; color: var(--muted); }

  /* ── CTA ── */
  .cv-cta-section {
    padding: 100px 5vw;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .cv-cta-section::before {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(201,168,76,0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  .cv-cta-section .cv-section-tag { justify-content: center; }
  .cv-cta-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(1.8rem, 4vw, 3.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.1;
    color: var(--ink);
    max-width: 580px;
    margin: 12px auto 16px;
  }
  .cv-cta-sub {
    font-size: 0.95rem;
    color: var(--muted);
    max-width: 380px;
    margin: 0 auto 40px;
    line-height: 1.75;
  }
  .cv-store-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .cv-store-btn {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 13px 24px;
    border-radius: 14px;
    border: 1.5px solid var(--ink);
    text-decoration: none;
    color: var(--ink);
    font-size: 0.82rem;
    font-weight: 600;
    transition: all 0.25s;
  }
  .cv-store-btn:hover {
    background: var(--ink);
    color: var(--paper);
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(10,10,15,0.18);
  }
  .cv-store-btn-icon { font-size: 1.4rem; line-height: 1; }
  .cv-store-btn-small { font-size: 0.6rem; color: var(--muted); display: block; margin-bottom: 1px; font-weight: 400; }
  .cv-store-btn:hover .cv-store-btn-small { color: rgba(245,242,235,0.55); }
  .cv-store-btn-label { font-size: 0.88rem; font-weight: 700; display: block; }

  /* ── FOOTER ── */
  .cv-footer {
    background: var(--ink);
    padding: 40px 5vw;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 24px;
  }
  .cv-footer-logo {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 1.1rem;
    color: var(--paper);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .cv-footer-logo-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); }
  .cv-footer-links {
    display: flex;
    gap: 24px;
    list-style: none;
    justify-content: center;
    flex-wrap: wrap;
  }
  .cv-footer-links a {
    font-size: 0.76rem;
    color: rgba(245,242,235,0.38);
    text-decoration: none;
    transition: color 0.2s;
    white-space: nowrap;
  }
  .cv-footer-links a:hover { color: var(--paper); }
  .cv-footer-copy {
    font-size: 0.72rem;
    color: rgba(245,242,235,0.28);
    text-align: right;
  }

  /* ── ANIMATIONS ── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes floatIn {
    from { opacity: 0; transform: translateX(40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }

  .reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal.visible { opacity: 1; transform: none; }
  .reveal-d1 { transition-delay: 0.1s; }
  .reveal-d2 { transition-delay: 0.2s; }
  .reveal-d3 { transition-delay: 0.3s; }
  .reveal-d4 { transition-delay: 0.4s; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .cv-steps-grid { grid-template-columns: repeat(2, 1fr); }
    .cv-features-grid { grid-template-columns: repeat(2, 1fr); }
    .cv-testimonials-grid { grid-template-columns: repeat(2, 1fr); }
    .cv-stats { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 768px) {
    .cv-nav-links { display: none; }
    .cv-hamburger { display: flex; }

    .cv-hero {
      grid-template-columns: 1fr;
      padding: 100px 5vw 60px;
      gap: 48px;
    }
    .cv-hero-right {
      animation: fadeUp 0.8s 0.4s ease both;
    }
    .cv-hero-title { font-size: clamp(2.5rem, 11vw, 4.5rem); }

    .cv-steps-grid { grid-template-columns: 1fr; border-radius: 16px; }
    .cv-features-grid { grid-template-columns: 1fr; }
    .cv-testimonials-grid { grid-template-columns: 1fr; }

    .cv-footer {
      grid-template-columns: 1fr;
      text-align: center;
      gap: 16px;
    }
    .cv-footer-copy { text-align: center; }
    .cv-footer-links { justify-content: center; }

    .cv-stats { grid-template-columns: repeat(2, 1fr); gap: 0; }
    .cv-stat { border-bottom: 1px solid rgba(245,242,235,0.07); padding: 24px 16px; }

    .cv-section-heading { margin-bottom: 36px; }
  }

  @media (max-width: 480px) {
    .cv-hero-actions { flex-direction: column; align-items: flex-start; }
    .cv-hero-title { font-size: clamp(2.2rem, 13vw, 3.5rem); }
    .cv-store-buttons { flex-direction: column; align-items: center; }
    .cv-store-btn { width: 100%; max-width: 280px; justify-content: center; }
    .cv-stats { grid-template-columns: repeat(2, 1fr); }
    .cv-testimonials-grid { grid-template-columns: 1fr; }
  }
`;

function CVMockup() {
  return (
    <div className="cv-mockup">
      <div className="cvm-header">
        <div className="cvm-avatar" />
        <div>
          <div className="cvm-name">Alexandra Chen</div>
          <div className="cvm-role">Senior Product Designer</div>
        </div>
      </div>
      <div className="cvm-section">
        <div className="cvm-section-label">Skills</div>
        {[['UI/UX Design', '92%'], ['Figma', '88%'], ['Research', '76%']].map(([l, w]) => (
          <div key={l} className="cvm-bar-row">
            <div className="cvm-bar-label">{l}</div>
            <div className="cvm-bar-track"><div className="cvm-bar-fill" style={{ width: w }} /></div>
          </div>
        ))}
      </div>
      <div className="cvm-section">
        <div className="cvm-section-label">Experience</div>
        <div className="cvm-line" />
        <div className="cvm-line m" />
        <div className="cvm-line s" />
      </div>
      <div className="cvm-section">
        <div className="cvm-section-label">Tags</div>
        <div className="cvm-badge-row">
          {['Product', 'Design', 'Strategy', 'Research'].map(t => (
            <span key={t} className="cvm-badge">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const steps = [
    { n: '01', icon: '🏷️', title: 'Choose Department', desc: 'Select from dozens of professional fields — tech, healthcare, finance, creative, and more.' },
    { n: '02', icon: '🎨', title: 'Pick a Template', desc: 'Browse ATS-friendly templates crafted to impress both recruiters and automated systems.' },
    { n: '03', icon: '✏️', title: 'Fill Your Info', desc: 'Enter your details with guided prompts. Smart suggestions make writing effortless.' },
    { n: '04', icon: '⬇️', title: 'Preview & Download', desc: 'Preview your polished CV and download instantly in PDF or DOCX format.' },
  ];

  const features = [
    { icon: '⚡', title: 'Instant Generation', desc: 'A complete, professional CV in under 5 minutes with our step-by-step guided flow.' },
    { icon: '🎯', title: 'ATS Optimised', desc: 'Every template is engineered to pass applicant tracking systems used by top companies.' },
    { icon: '🌍', title: 'Multi-Language', desc: 'Create CVs in 20+ languages and tailor content for global job markets with ease.' },
    { icon: '📊', title: 'Skills Visualizer', desc: 'Showcase expertise with beautiful skill bars, progress charts, and keyword tags.' },
    { icon: '☁️', title: 'Cloud Sync', desc: 'Save multiple CVs, access from any device, and update your profile anytime.' },
    { icon: '🔒', title: 'Private & Secure', desc: 'Your data stays yours. We never share or sell personal information to anyone.' },
  ];

  const testimonials = [
    { stars: '★★★★★', text: '"I landed interviews at 3 top companies within a week of using CV Maker. The templates are stunning and really stood out."', name: 'Sarah M.', role: 'Software Engineer', initial: 'S' },
    { stars: '★★★★★', text: '"Finally an app that actually makes resume building simple. Downloaded, filled in my info, done in 10 minutes. Got the job!"', name: 'James K.', role: 'Marketing Manager', initial: 'J' },
    { stars: '★★★★☆', text: '"The ATS optimization feature is a game-changer. My response rate from applications doubled after switching to CV Maker."', name: 'Priya N.', role: 'Data Analyst', initial: 'P' },
  ];

  return (
    <>
      <style>{styles}</style>

      {/* NAV */}
      <nav className="cv-nav">
        <a className="cv-nav-logo" href="#">
          <img src="/assets/images/logo.png" alt="CV Maker" style={{ width: '60px', height: '60px', border: '2px solid #e8471d', borderRadius: '50%' }} />
          CV Maker
        </a>
        <ul className="cv-nav-links">
          <li><a href="#">Home</a></li>
          <li><a href="#steps">How It Works</a></li>
          <li><a href="#features">Features</a></li>
          <li><a href="/privacy-policy">Privacy</a></li>
          <li><a className="cv-nav-cta" href="#download" >Download</a></li>
        </ul>
        <button className={`cv-hamburger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* MOBILE MENU */}
      <div className={`cv-mobile-menu ${menuOpen ? 'open' : ''}`}>
        {['Home', 'How It Works', 'Features', 'Privacy'].map((l, i) => (
          <a key={l} href={i === 0 ? '#' : i === 1 ? '#steps' : i === 2 ? '#features' : '/privacy-policy'}
            onClick={() => setMenuOpen(false)}>{l}</a>
        ))}
        <a className="cv-mobile-cta" href="#download"  onClick={() => setMenuOpen(false)}>Download Free</a>
      </div>

      {/* HERO */}
      <section className="cv-hero">
        <div className="cv-hero-left">
          <div className="cv-hero-eyebrow">The Resume Builder That Works</div>
          <h1 className="cv-hero-title">
            Land your<br />dream <em>job</em><br />faster.
          </h1>
          <p className="cv-hero-sub">Create a professional CV in minutes. Pick a template, fill your details, download and get hired.</p>
          <div className="cv-hero-actions">
            <a className="cv-btn-primary" href="#download" >
              📲 Download Free
            </a>
            <a className="cv-btn-ghost" href="#steps">See how it works →</a>
          </div>
        </div>
        <div className="cv-hero-right">
          <div className="cv-mockup-wrap"><CVMockup /></div>
        </div>
      </section>

      {/* STATS */}
      <div className="cv-stats">
        {[['1M', '+', 'Downloads'], ['50', '+', 'Templates'], ['4.8', '★', 'App Rating'], ['98', '%', 'Success Rate']].map(([n, s, l]) => (
          <div key={l} className="cv-stat reveal">
            <div className="cv-stat-num"><b>{n}{s}</b></div>
            <div className="cv-stat-label">{l}</div>
          </div>
        ))}
      </div>

      {/* STEPS */}
      <section className="cv-steps-section" id="steps">
        <div className="cv-section-tag reveal">Process</div>
        <h2 className="cv-section-heading reveal">Four steps to your perfect CV.</h2>
        <div className="cv-steps-grid">
          {steps.map((s, i) => (
            <div key={s.n} className={`cv-step-card reveal reveal-d${i + 1}`}>
              <div className="cv-step-num">{s.n}</div>
              <div className="cv-step-icon">{s.icon}</div>
              <div className="cv-step-title">{s.title}</div>
              <p className="cv-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="cv-features-section" id="features">
        <div className="cv-section-tag reveal">Features</div>
        <h2 className="cv-section-heading reveal">Everything you need<br />to get hired.</h2>
        <div className="cv-features-grid">
          {features.map((f, i) => (
            <div key={f.title} className={`cv-feature-card reveal reveal-d${(i % 4) + 1}`}>
              <span className="cv-feature-icon">{f.icon}</span>
              <div className="cv-feature-title">{f.title}</div>
              <p className="cv-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="cv-testimonials-section">
        <div className="cv-section-tag reveal">Testimonials</div>
        <h2 className="cv-section-heading reveal">Loved by job seekers<br />worldwide.</h2>
        <div className="cv-testimonials-grid">
          {testimonials.map((t, i) => (
            <div key={i} className={`cv-testimonial-card reveal reveal-d${i + 1}`}>
              <div className="cv-testimonial-stars">{t.stars}</div>
              <p className="cv-testimonial-text">{t.text}</p>
              <div className="cv-testimonial-author">
                <div className="cv-testimonial-avatar">{t.initial}</div>
                <div>
                  <div className="cv-testimonial-name">{t.name}</div>
                  <div className="cv-testimonial-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cv-cta-section" id="download">
        <div className="cv-section-tag reveal">Get Started</div>
        <h2 className="cv-cta-title reveal">Your next chapter starts with a great CV.</h2>
        <p className="cv-cta-sub reveal">Free to download. No sign-up required. Available on Android and iOS.</p>
        <div className="cv-store-buttons reveal">
          <a className="cv-store-btn" href="https://play.google.com/store/apps/details?id=com.cvmaker" >
            <span className="cv-store-btn-icon">🤖</span>
            <div><span className="cv-store-btn-small">Get it on</span><span className="cv-store-btn-label">Google Play</span></div>
          </a>
          <a className="cv-store-btn" href="https://apps.apple.com/us/app/nursing-cv-maker/id6736829950" >
            <span className="cv-store-btn-icon">🍎</span>
            <div><span className="cv-store-btn-small">Download on the</span><span className="cv-store-btn-label">App Store</span></div>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="cv-footer">
        <a className="cv-footer-logo" href="#">
          <span className="cv-footer-logo-dot" />
          CV Maker
        </a>
        <ul className="cv-footer-links">
          <li><a href="#">Home</a></li>
          <li><a href="#features">Features</a></li>
          <li><a href="/privacy-policy">Privacy Policy</a></li>
          <li><a href="https://www.facebook.com/nursingprometric994" >Contact</a></li>
        </ul>
        <div className="cv-footer-copy">© 2025 CV Maker. All rights reserved.</div>
      </footer> 
    </>
  );
}